import { Input } from 'src/components/UI/Forms/Inputs';
import { useForm } from 'react-hook-form';
import {
  AnyValidString,
  Email,
  PhoneRequired,
  SelectAnyValue,
} from 'src/utils/validations/formValidations';
import { useRegisterMutation } from 'src/services/auth/auth';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomSelect from 'src/components/UI/Forms/Inputs/customSelect';
import { Authorities } from 'src/data/LicenseIssuingAuthority';
import { useToast } from 'src/hooks/use-toast';
import DatePickerComponent from 'src/components/UI/Forms/Inputs/datePicker';
import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import FileUpload from 'src/components/UI/Forms/Inputs/fileUpload';
import PhoneInput from 'src/components/UI/Forms/Inputs/phoneInput';

interface ILoginProps { }

interface LocationState {
  regType: string;
}

export function Register(props: ILoginProps) {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onChange' });
  const navigate = useNavigate();
  const [registerUser] = useRegisterMutation();
  const location = useLocation();
  const state = location.state as LocationState;
  const { t } = useTranslation();
  const { regType } = state || {};
  const { showToast } = useToast();
  const errorToastActiveRef = useRef(false);

  const showSingleErrorToast = (message: string) => {
    if (errorToastActiveRef.current) return;
    errorToastActiveRef.current = true;
    showToast({
      title: message,
      variant: 'danger',
      anchorOrigin: { vertical: 'top', horizontal: 'end' },
    });
    setTimeout(() => { errorToastActiveRef.current = false; }, 3500);
  };

  const [isRegistering, setIsRegistering] = useState(false);
  const [tradeLicenseExpiry, setTradeLicenseExpiry] = useState<Date | null>(
    null
  );
  const [siraCertExpiry, setSiraCertExpiry] = useState<Date | null>(null);
  const [commRegExpiry, setCommRegExpiry] = useState<Date | null>(null);
  const [insuranceExpiry, setInsuranceExpiry] = useState<Date | null>(null);
  const [tlExpiryError, setTlExpiryError] = useState(false);
  const [siraExpiryError, setSiraExpiryError] = useState(false);
  const [commRegExpiryError, setCommRegExpiryError] = useState(false);
  const [insuranceExpiryError, setInsuranceExpiryError] = useState(false);
  const [tlFileError, setTlFileError] = useState(false);
  const [siraFileError, setSiraFileError] = useState(false);
  const [commRegFileError, setCommRegFileError] = useState(false);
  const [insuranceFileError, setInsuranceFileError] = useState(false);
  const [tlFile, setTlFile] = useState<File | null>(null);
  const [siraCertFile, setSiraCertFile] = useState<File | null>(null);
  const [commRegFile, setCommRegFile] = useState<File | null>(null);
  const [insuranceFile, setInsuranceFile] = useState<File | null>(null);

  // Single file change handler
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: string
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      switch (fileType) {
        case 'tlFile':
          setTlFile(file);
          setTlFileError(false);
          break;
        case 'siraCertFile':
          setSiraCertFile(file);
          setSiraFileError(false);
          break;
        case 'commRegFile':
          setCommRegFile(file);
          setCommRegFileError(false);
          break;
        case 'insuranceFile':
          setInsuranceFile(file);
          setInsuranceFileError(false);
          break;
        default:
          console.warn('Unknown file type:', fileType);
      }
    }
  };

  const formatDate = (date: Date | null) => {
    return date ? date.toISOString().slice(0, 10) : '';
  };

  const validateFilesAndDates = () => {
    let hasError = false;

    if (!tradeLicenseExpiry) {
      setTlExpiryError(true);
      hasError = true;
    } else {
      setTlExpiryError(false);
    }

    if (!siraCertExpiry) {
      setSiraExpiryError(true);
      hasError = true;
    } else {
      setSiraExpiryError(false);
    }

    if (!commRegExpiry) {
      setCommRegExpiryError(true);
      hasError = true;
    } else {
      setCommRegExpiryError(false);
    }

    if (!insuranceExpiry) {
      setInsuranceExpiryError(true);
      hasError = true;
    } else {
      setInsuranceExpiryError(false);
    }

    if (!tlFile) {
      setTlFileError(true);
      hasError = true;
    } else {
      setTlFileError(false);
    }

    if (!siraCertFile) {
      setSiraFileError(true);
      hasError = true;
    } else {
      setSiraFileError(false);
    }

    if (!commRegFile) {
      setCommRegFileError(true);
      hasError = true;
    } else {
      setCommRegFileError(false);
    }

    if (!insuranceFile) {
      setInsuranceFileError(true);
      hasError = true;
    } else {
      setInsuranceFileError(false);
    }

    return hasError;
  };

  const onInvalid = () => {
    validateFilesAndDates();
  };

  const onSubmit = async (data: any) => {
    const hasError = validateFilesAndDates();

    if (hasError) return;

    setIsRegistering(true);

    const formData = new FormData();

    // Append all text fields
    formData.append('type', regType || '');
    formData.append('name', data.name);
    formData.append('tradeLicenseIssuer', data.tradeLicenseIssuer);
    formData.append('tradeLicenseNumber', data.tradeLicenseNumber);
    formData.append('tradeLicenseExpiry', formatDate(tradeLicenseExpiry));
    formData.append('trnNumber', data.trnNumber);
    formData.append('siraCertExpiry', formatDate(siraCertExpiry));
    formData.append('commRegExpiry', formatDate(commRegExpiry));
    formData.append('insuranceExpiry', formatDate(insuranceExpiry));
    formData.append('firstName', data.firstName);
    formData.append('lastName', data.lastName);
    formData.append('phone', `+971${data.phone}`);
    formData.append('email', data.email.toLowerCase());
    formData.append('reviewed', 'true');

    // Append files if they exist
    if (tlFile) {
      formData.append('tlFile', tlFile);
    }
    if (siraCertFile) {
      formData.append('siraCertFile', siraCertFile);
    }
    if (commRegFile) {
      formData.append('commRegFile', commRegFile);
    }
    if (insuranceFile) {
      formData.append('insuranceFile', insuranceFile);
    }

    console.log("Submitting Registration Payload:", Array.from(formData.entries()));

    try {
      const response = await registerUser(formData).unwrap();
      if (response.message === 'User registered') {
        reset();
        setTlFile(null);
        setSiraCertFile(null);
        setCommRegFile(null);
        setInsuranceFile(null);
        navigate('/login', {
          state: {
            fromRegister: true,
            snackbarMessage:
              'Success! Your account is registered. Log in to get started.',
            snackbarSeverity: 'success',
          },
        });
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      const message = error?.data?.message || 'Registration failed. Try again.';
      showSingleErrorToast(message);
    } finally {
      setIsRegistering(false);
    }
  };

  const onGoToLogin = () => {
    navigate('/login');
  };

  return (
    <>
      <div
        className="bg-login relative"
        style={{ backgroundColor: "var(--bg-base)" }}
      >
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <div
            className="absolute inset-0 dark:hidden"
            style={{
              backgroundImage: 'url("/assets/images/login_bg_light.png")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>
          <div
            className="absolute inset-0 hidden dark:block"
            style={{
              backgroundImage: 'url("/assets/images/login_bg.png")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>
          <div
            className="absolute inset-0 dark:hidden"
            style={{
              background:
                'radial-gradient(circle at 20% 20%, rgba(16,185,129,0.08), transparent 28%), radial-gradient(circle at 82% 18%, rgba(59,130,246,0.06), transparent 24%), linear-gradient(180deg, rgba(245,248,252,0.78) 0%, rgba(232,239,247,0.88) 100%)',
            }}
          ></div>
          <div
            className="absolute inset-0 hidden dark:block"
            style={{
              background:
                'radial-gradient(circle at 18% 22%, rgba(16,185,129,0.12), transparent 30%), radial-gradient(circle at 78% 18%, rgba(56,189,248,0.08), transparent 24%), linear-gradient(180deg, rgba(15,23,31,0.78) 0%, rgba(20,29,39,0.9) 100%)',
            }}
          ></div>
        </div>

        <div className="h-screen relative z-10 py-8">
          <div
            className="rounded-2xl w-register mx-auto pt-2 pb-4 px-6 md:px-12 relative max-h-[90vh] overflow-y-scroll custom-scroll"
            style={{
              backgroundColor: "var(--bg-surface)",
              border: "1px solid var(--border-light)",
              boxShadow: "var(--shadow-card)"
            }}
          >
            <div className="py-6">
              <img
                src="assets/images/murafiq_logo.png"
                alt="logo"
                className="mx-auto h-28 md:px-24 invert dark:invert-0"
              />
            </div>
            <h2 className="text-center font-semibold text-xl" style={{ color: "var(--text-main)" }}>
              {t('login_page.title')}
            </h2>
            <p className="text-sm text-center opacity-70 mb-8" style={{ color: "var(--text-muted)" }}>
              Please fill the form to continue.
            </p>
            <form
              onSubmit={handleSubmit(onSubmit, onInvalid)}
              encType="multipart/form-data"
            >
              <div className="flex-row">
                <Input
                  type="text"
                  name="name"
                  label="Company Name"
                  placeholder="Enter company name"
                  className="mb-6 flex-1"
                  inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                  register={register}
                  defaultValue={''}
                  validation={AnyValidString}
                  error={errors.name}
                  errorText="Company name is required"
                />
                {regType === 'transporter' ? (
                  <Input
                    type="text"
                    name="haulierCode"
                    label="Haulier Code"
                    placeholder="Enter haulier code"
                    className="mb-6 flex-1"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    defaultValue={''}
                    validation={AnyValidString}
                    error={errors.haulierCode}
                    errorText="Haulier code is required"
                  />
                ) : null}
              </div>

              <CustomSelect
                name="tradeLicenseIssuer"
                label="License Issuing Authority"
                labelClassName="!text-[var(--text-muted)] !mb-2"
                options={Authorities}
                placeholder="Select license issuing authority"
                className="mb-8"
                selectClassName="shadow-sm border border-[var(--border-light)] placeholder:text-[var(--text-muted)] text-[var(--text-main)]"
                register={register}
                validation={SelectAnyValue}
                setValue={setValue}
                defaultValue={''}
                error={errors.tradeLicenseIssuer}
                // disabled={!!(user?.company?.tradeLicenseIssuer)}
                errorText="License issuing authority is required"
              ></CustomSelect>
              <FileUpload
                id="tlFile"
                name="tlFile"
                label="Trade License File"
                inputClassName="shadow-sm border border-dashed border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]  !text-sm !mb-2"
                placeholder="Upload Trade License Copy"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFileChange(e, 'tlFile')
                }
                register={register}
                allowedFileTypes={[
                  'image/png',
                  'image/jpeg',
                  'application/pdf',
                ]} // Allowed types
                maxFileSize={1024 * 1024} // Max file size 1MB
                error={tlFileError ? ({ type: 'required' } as any) : undefined}
                errorText="Trade license file is required"
              />
              <div className="flex-row">
                <Input
                  type="text"
                  name="tradeLicenseNumber"
                  label="Trade License Number"
                  placeholder="Enter trade license number"
                  className="mb-6 flex-1"
                  inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                  register={register}
                  defaultValue={''}
                  validation={AnyValidString}
                  error={errors.tradeLicenseNumber}
                  errorText="Trade license number is required"
                />
                <DatePickerComponent
                  selectedDate={tradeLicenseExpiry}
                  onChange={(date: Date | null) => {
                    setTradeLicenseExpiry(date);
                    if (date) setTlExpiryError(false);
                  }}
                  isDateError={tlExpiryError}
                  minDate={new Date()}
                  label={'Trade License Expiry Date'}
                  placeholder="MM/DD/YYYY"
                  errorText="Trade license expiry date is required"
                  className="flex-1 mb-6"
                />
              </div>
              <Input
                type="text"
                name="trnNumber"
                label="TRN Number"
                placeholder="Enter TRN number"
                className="mb-6 flex-1"
                inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                register={register}
                defaultValue={''}
                validation={AnyValidString}
                error={errors.trnNumber}
                errorText="TRN number is required"
              />

              <FileUpload
                id="siraCertFile"
                name="siraCertFile"
                label="SIRA Certificate"
                inputClassName="shadow-sm border border-dashed border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]  !text-sm !mb-2"
                placeholder="Upload SIRA Certificate Copy"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFileChange(e, 'siraCertFile')
                }
                register={register}
                allowedFileTypes={[
                  'image/png',
                  'image/jpeg',
                  'application/pdf',
                ]} // Allowed types
                maxFileSize={1024 * 1024} // Max file size 1MB
                error={siraFileError ? ({ type: 'required' } as any) : undefined}
                errorText="SIRA certificate file is required"
              />
              <DatePickerComponent
                selectedDate={siraCertExpiry}
                onChange={(date: Date | null) => {
                  setSiraCertExpiry(date);
                  if (date) setSiraExpiryError(false);
                }}
                isDateError={siraExpiryError}
                minDate={new Date()}
                label={'SIRA Certificate Expiry Date'}
                placeholder="MM/DD/YYYY"
                errorText="SIRA certificate expiry date is required"
                className="flex-1 mb-6"
              />
              <FileUpload
                id="commRegFile"
                name="commRegFile"
                label="Commercial Registration File"
                inputClassName="shadow-sm border border-dashed border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]  !text-sm !mb-2"
                placeholder="Upload Comm Reg File Copy"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFileChange(e, 'commRegFile')
                }
                register={register}
                allowedFileTypes={[
                  'image/png',
                  'image/jpeg',
                  'application/pdf',
                ]} // Allowed types
                maxFileSize={1024 * 1024} // Max file size 1MB
                error={commRegFileError ? ({ type: 'required' } as any) : undefined}
                errorText="Commercial registration file is required"
              />
              <DatePickerComponent
                selectedDate={commRegExpiry}
                onChange={(date: Date | null) => {
                  setCommRegExpiry(date);
                  if (date) setCommRegExpiryError(false);
                }}
                isDateError={commRegExpiryError}
                minDate={new Date()}
                label={'Commercial Registration Expiry Date'}
                placeholder="MM/DD/YYYY"
                errorText="Commercial registration expiry date is required"
                className="flex-1 mb-6"
              />
              <FileUpload
                id="insuranceFile"
                name="insuranceFile"
                label="Insurance File"
                inputClassName="shadow-sm border border-dashed border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]  !text-sm !mb-2"
                placeholder="Upload Insurance File Copy"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFileChange(e, 'insuranceFile')
                }
                register={register}
                allowedFileTypes={[
                  'image/png',
                  'image/jpeg',
                  'application/pdf',
                ]} // Allowed types
                maxFileSize={1024 * 1024} // Max file size 1MB
                error={insuranceFileError ? ({ type: 'required' } as any) : undefined}
                errorText="Insurance file is required"
              />
              <DatePickerComponent
                selectedDate={insuranceExpiry}
                onChange={(date: Date | null) => {
                  setInsuranceExpiry(date);
                  if (date) setInsuranceExpiryError(false);
                }}
                isDateError={insuranceExpiryError}
                minDate={new Date()}
                label={'Insurance Expiry Date'}
                placeholder="MM/DD/YYYY"
                errorText="Insurance expiry date is required"
                className="flex-1 mb-6"
              />
              <div className="flex-row">
                <Input
                  type="text"
                  name="firstName"
                  label="First Name"
                  placeholder="Enter your first name"
                  className="mb-6 flex-1"
                  inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                  register={register}
                  defaultValue={''}
                  validation={AnyValidString}
                  error={errors.firstName}
                  errorText="First name is required"
                />
                <Input
                  type="text"
                  name="lastName"
                  label="Last Name"
                  placeholder="Enter your last name"
                  className="mb-6 flex-1"
                  inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                  register={register}
                  defaultValue={''}
                  validation={AnyValidString}
                  error={errors.lastName}
                  errorText="Last name is required"
                />
              </div>
              <div className="flex-row">
                <PhoneInput
                  disableCountryCode
                  type="tel"
                  name="phone"
                  label={t('user.contact_number')}
                  placeholder="55XXXXXXX"
                  className="w-full !mb-6"
                  inputClassName="bg-transparent shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)] !w-full"
                  prefixClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                  register={register}
                  validation={PhoneRequired}
                  error={errors.phone}
                  errorText={t('user.error_messages.phone_number')}
                />
              </div>
              <div className="flex-row">
                {/* <Input
                  type="tel"
                  name="phone"
                  label="Phone"
                  placeholder="97155XXXXXXX"
                  className="mb-6 flex-1"
                  inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                  register={register}
                  defaultValue={''}
                  validation={Phone}
                  error={errors.phone}
                  errorText="Enter valid phone number"
                /> */}
                <Input
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="Enter your email"
                  className="mb-6 flex-1"
                  inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                  register={register}
                  defaultValue={''}
                  validation={Email}
                  error={errors.email}
                  errorText="Enter valid email address"
                />
              </div>
              <Button
                variant="solid"
                type="submit"
                className="text-md w-full rounded p-6 mb-8 disabled:opacity-50 shadow-lg font-semibold transition-colors duration-200" style={{ backgroundColor: "var(--color-primary)", color: "var(--text-invert)" }}
                disabled={isRegistering}
              >
                Register
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  className="underline cursor-pointer inline-block opacity-80" style={{ color: "var(--text-main)" }}
                  onClick={onGoToLogin}
                >
                  Back to login
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

    </>
  );
}
